import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Fab,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Slide,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { useAuth } from 'contexts/AuthContext';

const ChatSupport = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      text: `Hi${user?.firstName ? ` ${user.firstName}` : ''}! 👋 I'm your AccuSmart assistant. How can I help you today?`,
      time: new Date()
    }
  ]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const getAIResponse = async (userMessage) => {
    // System context for the AI
    const systemContext = `You are AccuSmart's helpful AI assistant for the AccuSmart Meter Dashboard. 
You help users with:
- Navigating the dashboard and finding features
- Understanding how to manage projects, meters, floors, and suites
- Commissioning meters and tracking progress
- Managing users and organizations
- Technical questions about smart meters

Keep responses concise, friendly, and helpful. If you don't know something specific about AccuSmart, 
suggest the user contact support or check the documentation.

Current user: ${user?.firstName || 'User'} ${user?.lastName || ''} (${user?.role || 'Member'})`;

    try {
      // Call your backend AI endpoint
      const response = await fetch('http://localhost:3000/chat/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          message: userMessage,
          context: systemContext,
          history: messages.slice(-10).map(m => ({
            role: m.type === 'user' ? 'user' : 'assistant',
            content: m.text
          }))
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      return data.response;
    } catch (error) {
      console.error('AI chat error:', error);
      // Fallback responses when API is unavailable
      return getFallbackResponse(userMessage);
    }
  };

  const getFallbackResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('project')) {
      return "To manage projects, go to **Projects** in the sidebar. You can create new projects, view details, and track commissioning progress there.";
    }
    if (lowerMessage.includes('meter')) {
      return "For meter management, use the **Building Structure** page to add meters to suites. You can also configure **Meter Types** from the sidebar.";
    }
    if (lowerMessage.includes('commission')) {
      return "Check the **Commissioning Summary** page to see progress by project. You can commission meters from the Building Structure page.";
    }
    if (lowerMessage.includes('user') || lowerMessage.includes('team')) {
      return "User management is available under **Administration > User Management** if you have admin permissions.";
    }
    if (lowerMessage.includes('help') || lowerMessage.includes('how')) {
      return "I can help you with:\n• Managing projects and buildings\n• Commissioning meters\n• User management\n• Navigation tips\n\nWhat would you like to know more about?";
    }
    if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
      return `Hello! How can I assist you with AccuSmart today?`;
    }
    
    return "I'm here to help with AccuSmart Metering Solutions Dashboard. You can ask me about projects, meters, commissioning, or navigation. For complex issues, please contact support@accusmartmeters.com";
  };

  const handleSend = async () => {
    if (!message.trim() || loading) return;

    const userMessage = message.trim();
    setMessage('');
    
    // Add user message
    const userMsg = {
      id: Date.now(),
      type: 'user',
      text: userMessage,
      time: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    
    setLoading(true);
    
    try {
      const aiResponse = await getAIResponse(userMessage);
      
      // Add bot response
      const botMsg = {
        id: Date.now() + 1,
        type: 'bot',
        text: aiResponse,
        time: new Date()
      };
      setMessages(prev => [...prev, botMsg]);
    } catch {
      const errorMsg = {
        id: Date.now() + 1,
        type: 'bot',
        text: "Sorry, I'm having trouble connecting. Please try again or contact support.",
        time: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  return (
    <>
      {/* Chat Window */}
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 20,
            width: { xs: 'calc(100% - 40px)', sm: 380 },
            maxWidth: 380,
            height: 500,
            maxHeight: 'calc(100vh - 120px)',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 3,
            overflow: 'hidden',
            zIndex: 1300
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'primary.dark', width: 36, height: 36 }}>
                <BotIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  AccuSmart Support
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  AI Assistant • Online
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleToggle} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 2,
              bgcolor: 'grey.50',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: msg.type === 'user' ? 'row-reverse' : 'row',
                  gap: 1,
                  alignItems: 'flex-end'
                }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: msg.type === 'user' ? 'secondary.main' : 'primary.main',
                    fontSize: '0.875rem'
                  }}
                >
                  {msg.type === 'user' ? (
                    user?.firstName?.[0] || <PersonIcon fontSize="small" />
                  ) : (
                    <BotIcon fontSize="small" />
                  )}
                </Avatar>
                <Box
                  sx={{
                    maxWidth: '75%',
                    bgcolor: msg.type === 'user' ? 'primary.main' : 'white',
                    color: msg.type === 'user' ? 'white' : 'text.primary',
                    p: 1.5,
                    borderRadius: 2,
                    borderBottomRightRadius: msg.type === 'user' ? 0.5 : 2,
                    borderBottomLeftRadius: msg.type === 'user' ? 2 : 0.5,
                    boxShadow: 1
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      '& strong': { fontWeight: 600 }
                    }}
                    dangerouslySetInnerHTML={{ 
                      __html: msg.text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') 
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      opacity: 0.7,
                      textAlign: msg.type === 'user' ? 'right' : 'left'
                    }}
                  >
                    {formatTime(msg.time)}
                  </Typography>
                </Box>
              </Box>
            ))}
            
            {loading && (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Avatar sx={{ width: 28, height: 28, bgcolor: 'primary.main' }}>
                  <BotIcon fontSize="small" />
                </Avatar>
                <Box
                  sx={{
                    bgcolor: 'white',
                    p: 1.5,
                    borderRadius: 2,
                    borderBottomLeftRadius: 0.5,
                    boxShadow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Typing...
                  </Typography>
                </Box>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input */}
          <Divider />
          <Box sx={{ p: 1.5, bgcolor: 'white', display: 'flex', gap: 1 }}>
            <TextField
              ref={inputRef}
              fullWidth
              size="small"
              placeholder="Type your message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!message.trim() || loading}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'grey.300', color: 'grey.500' }
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      </Slide>

      {/* Floating Button */}
      <Fab
        color="primary"
        onClick={handleToggle}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          zIndex: 1300,
          boxShadow: 4,
          '&:hover': {
            transform: 'scale(1.05)'
          },
          transition: 'transform 0.2s'
        }}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </Fab>
    </>
  );
};

export default ChatSupport;
